'use client';

import React, { isValidElement, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import { ChevronDownIcon } from '@milanote/roma-icons';
import { Field } from '../field';
import { cx, sortCx } from '../../../utils/cx';
import { isReactComponent } from '../../../utils/isReactComponent';

const SEMANTIC_NAME = 'RomaSelectField';

export type SelectSize = 'sm' | 'md' | 'lg';

export const styles = sortCx({
    common: {
        shell: [
            'relative flex w-full items-center overflow-hidden',
            'bg-primary',
            'ring-1 ring-primary ring-inset outline-hidden',
            'transition duration-100 ease-linear',
            'has-[:focus]:ring-primary_focused has-[[data-focus-visible]]:ring-2',
        ].join(' '),
        control: 'flex w-full min-w-0 items-center appearance-none bg-transparent outline-hidden cursor-pointer',
        leadingIconWrapper: 'pointer-events-none absolute z-10',
        leadingIcon: 'shrink-0 text-fg-quaternary',
        chevronWrapper: 'pointer-events-none absolute z-10 text-fg-quaternary',
        chevron: '',
    },
    sizes: {
        sm: {
            shell: 'rounded-xs',
            root: 'py-1.5 text-sm font-medium',
            defaultPadding: 'px-3',
            trailingPadding: 'pr-8',
            leadingIcon: 'size-5',
            leadingIconWrapper: 'left-3 top-1/2 -translate-y-1/2',
            leadingPadding: 'pl-10',
            chevron: 'size-4 stroke-[2.5px]',
            chevronWrapper: 'right-2.5 top-1/2 -translate-y-1/2',
            item: 'p-2 pl-2 text-sm',
            itemIcon: '*:data-icon:size-4 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary',
            check: 'size-4 stroke-[2.5px]',
            popover: 'max-h-64!',
        },
        md: {
            shell: 'rounded-xs',
            root: 'py-2.5 text-md font-medium',
            defaultPadding: 'px-3.5',
            trailingPadding: 'pr-9',
            leadingIcon: 'size-5',
            leadingIconWrapper: 'left-3.5 top-1/2 -translate-y-1/2',
            leadingPadding: 'pl-10.5',
            chevron: 'size-5',
            chevronWrapper: 'right-2.5 top-1/2 -translate-y-1/2',
            item: 'p-2.5 pl-2.5 text-md',
            itemIcon: '*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary',
            check: 'size-5',
            popover: 'max-h-80!',
        },
        lg: {
            shell: 'rounded-sm',
            root: 'py-2.5 text-lg font-medium',
            defaultPadding: 'px-3.5',
            trailingPadding: 'pr-10',
            leadingIcon: 'size-6',
            leadingIconWrapper: 'left-3.5 top-1/2 -translate-y-1/2',
            leadingPadding: 'pl-10.5',
            chevron: 'size-6',
            chevronWrapper: 'right-2.5 top-1/2 -translate-y-1/2',
            item: 'p-3 pl-3 text-lg',
            itemIcon: '*:data-icon:size-6 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary',
            check: 'size-6',
            popover: 'max-h-96!',
        },
    },
});

export type SelectFieldProps = {
    label?: string;
    hint?: string;
    tooltip?: string;
    className?: string;
    labelFor?: string;
    labelId?: string;
    hintId?: string;
    isRequired?: boolean;
    isInvalid?: boolean;
    isDisabled?: boolean;
    size?: SelectSize;
    leadingIcon?: FC | ReactNode;
    children: ReactNode;
};

export const SelectField = ({
    label,
    hint,
    tooltip,
    className,
    labelFor,
    labelId,
    hintId,
    isRequired,
    isInvalid,
    isDisabled,
    size = 'sm',
    leadingIcon,
    children,
}: SelectFieldProps) => {
    const leadingIconNode = useMemo(() => {
        if (!leadingIcon) return null;

        if (isReactComponent(leadingIcon)) {
            const LeadingIcon = leadingIcon;
            return (
                <LeadingIcon
                    data-icon
                    aria-hidden="true"
                    className={cx(styles.common.leadingIcon, styles.sizes[size].leadingIcon)}
                />
            );
        }

        if (isValidElement(leadingIcon)) {
            return leadingIcon;
        }

        return null;
    }, [leadingIcon, size]);

    return (
        <Field className={cx(SEMANTIC_NAME, className)}>
            {label && (
                <Field.Label htmlFor={labelFor} id={labelId} tooltip={tooltip} isRequired={isRequired}>
                    {label}
                </Field.Label>
            )}

            <div
                className={cx(
                    styles.common.shell,
                    styles.sizes[size].shell,
                    isDisabled && 'cursor-default bg-disabled_subtle text-disabled',
                    isInvalid && 'ring-error_subtle has-[:focus]:ring-error',
                )}
            >
                {leadingIconNode && (
                    <div className={cx(styles.common.leadingIconWrapper, styles.sizes[size].leadingIconWrapper)}>
                        {leadingIconNode}
                    </div>
                )}

                {children}

                <ChevronDownIcon
                    aria-hidden="true"
                    className={cx(
                        styles.common.chevronWrapper,
                        styles.sizes[size].chevronWrapper,
                        styles.sizes[size].chevron,
                    )}
                />
            </div>

            {hint && (
                <Field.HintText id={hintId} isInvalid={isInvalid}>
                    {hint}
                </Field.HintText>
            )}
        </Field>
    );
};

SelectField.displayName = SEMANTIC_NAME;
